import React, { useEffect, useMemo } from "react";
import { useUser } from "@/app/providers";
import { string, object } from "prop-types";
import { useGetGroups, useUpdateProfile } from "@/hooks";
import { SubsidiaryDropdown } from "@/components";
import type { SxProps, Theme } from "@mui/material/styles";

type SubsidiaryDropdownAutoSaveProps = {
  componentLabel?: string;
  sx?: SxProps<Theme>;
};

type SubsidiaryChangeEvent = {
  label?: string;
  value?: string;
};

/**
 * Subsidiary dropdown component
 * @param {object} props props
 * @returns {React.JSX.Element} React Component
 * @class
 */
export const SubsidiaryDropdownAutoSave = (
  props: SubsidiaryDropdownAutoSaveProps,
) => {
  const { updateProfile } = useUpdateProfile();
  const { user, setUser } = useUser();
  const {
    currentSubsidiaryId,
    currentSubsidiary,
    currentSubsidiaryKey,
    currentTeamId,
  } = user || {};

  // const { customers: allCompanies } = useCompanies({
  //   customerId: currentTeamId,
  // });

  // const parentTeam = useMemo(() => {
  //   return allCompanies[0]?.name || "";
  // }, [allCompanies]);

  const { subsidiary } = useGetGroups({
    customerId: currentTeamId,
  });

  // Only show "All" option when there are 2 or more subsidiaries
  const showAll = useMemo(() => {
    return subsidiary?.length > 1;
  }, [subsidiary]);

  // Check if current selection is valid when subsidiaries change
  useEffect(() => {
    if (subsidiary && subsidiary.length > 0) {
      const activeKey = currentSubsidiaryKey || currentSubsidiaryId;
      if (activeKey === "all") {
        if (currentSubsidiaryId !== "all" || currentSubsidiary !== "All") {
          setUser((prev: any) => ({
            ...prev,
            currentSubsidiaryKey: "all",
            currentSubsidiary: "All",
            currentSubsidiaryId: "all",
          }));
        }
        return;
      }

      // Check if currentSubsidiary exists in the subsidiary list
      let foundSubsidiary = subsidiary.find(
        (sub: any) =>
          sub.id === activeKey ||
          (sub as any).key === activeKey ||
          (sub.country &&
            sub.cvr &&
            `${sub.country}${sub.cvr}`.toUpperCase() ===
              String(activeKey).toUpperCase()),
      );

      if (!foundSubsidiary && currentSubsidiary && activeKey !== "all") {
        foundSubsidiary = subsidiary.find(
          (sub: any) => sub.name === currentSubsidiary,
        );
      }

      if (foundSubsidiary) {
        const subKey =
          (foundSubsidiary as any).key ||
          (foundSubsidiary.country && foundSubsidiary.cvr
            ? `${foundSubsidiary.country.toUpperCase()}${foundSubsidiary.cvr}`
            : foundSubsidiary.id);
        if (
          foundSubsidiary.id !== currentSubsidiaryId ||
          subKey !== currentSubsidiaryKey
        ) {
          setUser((prev: any) => ({
            ...prev,
            currentSubsidiaryKey: subKey,
            currentSubsidiary: foundSubsidiary.name,
            currentSubsidiaryId: foundSubsidiary.id,
          }));
        }
      } else if (activeKey !== "all") {
        // Selection invalid, reset to "all" (if supported) or first subsidiary
        const newSubsidiary = showAll
          ? { id: "all", name: "All", key: "all" }
          : subsidiary[0];

        if (newSubsidiary) {
          const subKey = (newSubsidiary as any).key || newSubsidiary.id;
          setUser((prev: any) => ({
            ...prev,
            currentSubsidiaryKey: subKey,
            currentSubsidiary: newSubsidiary.name,
            currentSubsidiaryId: newSubsidiary.id,
          }));
          updateProfile({
            currentSubsidiaryKey: subKey,
          });
        }
      }
    }
  }, [
    subsidiary,
    currentSubsidiaryId,
    currentSubsidiaryKey,
    currentSubsidiary,
    showAll,
    setUser,
    updateProfile,
  ]);

  const handleChange = (event: SubsidiaryChangeEvent) => {
    if (
      event.value &&
      (event.value !== currentSubsidiaryId ||
        (event as any).key !== currentSubsidiaryKey) &&
      event.value !== ""
    ) {
      const subsidiaryKey =
        event.value === "all" ? "all" : (event as any).key || event.value;
      setUser((prev: any) => ({
        ...prev,
        currentSubsidiaryKey: subsidiaryKey,
        currentSubsidiary: event.value === "all" ? "All" : event.label,
        currentSubsidiaryId: event.value,
      }));
      updateProfile({
        currentSubsidiaryKey: subsidiaryKey,
      });
    }
  };

  return (
    <SubsidiaryDropdown
      defaultValue={currentSubsidiaryKey || currentSubsidiaryId}
      showAll={showAll}
      required
      onChange={handleChange}
      componentLabel={props.componentLabel}
      currentSubsidiary={currentSubsidiary}
      // currentTeam={currentTeam}
      currentTeamId={currentTeamId}
      sx={{
        ...props.sx,
      }}
    />
  );
};

SubsidiaryDropdownAutoSave.propTypes = {
  componentLabel: string,
  sx: object,
};
